import { useToken } from 'hooks/Tokens'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { formatUTCDay } from 'utils/analytics/date'
import { formatAmount, formatDollarAmount } from 'utils/formatDollarAmt'

const TooltipContainer = styled.div`
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  max-width: 340px;
  min-width: 280px;
`

const TooltipHeader = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  margin-bottom: 4px;
`

const TooltipSubHeader = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 8px;
`

const Divider = styled.hr`
  margin: 8px 0;
  border: 0.5px solid ${({ theme }) => theme.backgroundOutline};
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`

const Label = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
`

const Value = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  text-align: right;
  padding-left: 8px;
`

const SuccessValue = styled(Value)`
  color: ${({ theme }) => theme.accentSuccess};
`

interface BeraPerRewardTooltipProps {
  active?: boolean
  payload?: any[]
  label?: any
  stakingToken?: any
  pricePoints?: any[]
  rewardVaultRewardSymbol?: string
}

export const BeraPerRewardTooltip: React.FC<BeraPerRewardTooltipProps> = ({
  active,
  payload,
  label,
  stakingToken,
  pricePoints,
  rewardVaultRewardSymbol,
}) => {
  const data = payload?.[0]?.payload
  const rewardSymbol = rewardVaultRewardSymbol || 'Reward'

  const { wrapperAddress, isWrapperClaim, formattedDateTime } = useMemo(() => {
    if (!data) {
      return {
        originalCompound: null,
        wrapperAddress: null,
        isWrapperClaim: false,
        timestamp: Date.now(),
        formattedDateTime: formatUTCDay(Date.now() / 1000, 'MMM D, YYYY HH:mm'),
      }
    }

    const originalCompound = data.compoundNumber
      ? pricePoints?.find((p) => p.compoundNumber === data.compoundNumber)
      : null

    const wrapperAddress = originalCompound?.wrapperAddress
    const isWrapperClaim = data.isWrapperClaim || false

    const timestamp = data.timestamp ? data.timestamp : Date.now()
    const formattedDateTime = formatUTCDay(timestamp / 1000, 'MMM D, YYYY HH:mm')

    return {
      originalCompound,
      wrapperAddress,
      isWrapperClaim,
      timestamp,
      formattedDateTime,
    }
  }, [data, pricePoints])

  const wrapperToken = useToken(wrapperAddress)

  const displayValues = useMemo(() => {
    if (!data) {
      return {
        symbol: rewardSymbol,
        beraPerReward: '0.0000',
        tokenAmount: '0',
        formattedTokenAmount: '0',
        bountyAmount: '0',
        bountyValue: '0',
        claimType: 'Direct Claim',
      }
    }

    const symbol = isWrapperClaim ? wrapperToken?.symbol : rewardSymbol
    const tokenAmount = data.tokenAmount || 0
    const formattedTokenAmount = formatAmount(tokenAmount)
    const bountyAmount = formatAmount(data.bountyAmount || 0)
    const bountyValue = data.bountyValue || 0
    const claimType = isWrapperClaim ? 'Wrapper Claim' : 'Direct Claim'

    const beraPerReward = parseFloat(data.beraPerReward || 0).toFixed(6)

    return {
      symbol,
      beraPerReward,
      tokenAmount,
      formattedTokenAmount,
      bountyAmount,
      bountyValue: formatAmount(bountyValue),
      claimType,
    }
  }, [data, isWrapperClaim, rewardSymbol, wrapperToken])

  if (!active || !payload || !payload.length) {
    return null
  }

  return (
    <TooltipContainer>
      <TooltipHeader>Compound #{data.compoundNumber}</TooltipHeader>
      <TooltipSubHeader>{formattedDateTime}</TooltipSubHeader>

      <Divider />

      <InfoRow>
        <Label>{isWrapperClaim ? `${wrapperToken?.symbol}/BERA Cost:` : `${rewardSymbol}/BERA Cost:`}</Label>
        <SuccessValue>{displayValues.beraPerReward} BERA</SuccessValue>
      </InfoRow>

      <InfoRow>
        <Label>{isWrapperClaim ? `${wrapperToken?.symbol} Claimed:` : `${rewardSymbol} Claimed:`}</Label>
        <Value>
          {displayValues.formattedTokenAmount} {displayValues.symbol}
        </Value>
      </InfoRow>

      <InfoRow>
        <Label>Bounty Cost:</Label>
        <Value>
          {displayValues.bountyAmount}({formatDollarAmount(data.bountyValue)})
        </Value>
      </InfoRow>

      {/* <InfoRow>
        <Label>Formula:</Label>
        <Value style={{ fontSize: '10px' }}>(Bounty $ ÷ {displayValues.symbol} Amount) ÷ BERA Price</Value>
      </InfoRow> */}

      <InfoRow>
        <Label>Claim Type:</Label>
        <Value>{displayValues.claimType}</Value>
      </InfoRow>
    </TooltipContainer>
  )
}

export default BeraPerRewardTooltip
